import React, { useState, useEffect } from "react"
import { Modal, Progress } from "antd"

/**
 * Props interface for the ProgressModal component
 */
interface ProgressModalProps {
  /** The title to display at the top of the modal */
  title: string
  /** A flag indicating whether the loading process is active */
  isLoading: boolean
  /** The duration in milliseconds over which the progress bar should fill */
  duration: number
}

/**
 * ProgressModal is a component that displays a modal with a progress bar.
 * The progress bar automatically updates based on a specified duration.
 *
 * @component
 * @param {ProgressModalProps} props - The props for the component.
 * @returns {JSX.Element} The rendered ProgressModal component.
 */
const ProgressModal: React.FC<ProgressModalProps> = ({
  title,
  isLoading,
  duration,
}) => {
  const [progress, setProgress] = useState<number>(0)

  useEffect(() => {
    if (isLoading) {
      const start: number = Date.now()

      const interval: NodeJS.Timeout = setInterval(() => {
        const elapsed: number = Date.now() - start
        const percent: number = Math.min((elapsed / duration) * 100, 100)
        setProgress(Math.round(percent))

        if (percent >= 100) {
          clearInterval(interval)
        }
      }, 100)

      return (): void => {
        clearInterval(interval)
      }
    } else {
      setProgress(0)
    }
  }, [isLoading, duration])

  return (
    <Modal title={title} open={isLoading} closable={false} footer={null}>
      <div className="flex w-full">
        <Progress percent={progress} status="active" strokeColor="#36C3ED" />
      </div>
    </Modal>
  )
}

export default ProgressModal
